import { BookingCardTheme } from './themesCrafted';
import { Calendar, Clock, MapPin, Hash, ArrowUpRight } from 'lucide-react';
import { formatDateLong, formatTime } from '@/utils/dateFormatting';

export interface BookingCardBackProps {
  serviceName: string | null;
  appointmentDate: string;
  appointmentTime: string;
  locationLabel: string | null;
  publicConfirmationCode: string;
  theme: BookingCardTheme;
}

export function BookingCardBack({
  serviceName,
  appointmentDate,
  appointmentTime,
  locationLabel,
  publicConfirmationCode,
  theme,
}: BookingCardBackProps) {
  const formattedDate = formatDateLong(appointmentDate);
  const formattedTime = formatTime(appointmentTime);

  return (
    <div
      className="w-full h-full rounded-2xl p-6 flex flex-col border-2"
      style={{
        background: `linear-gradient(135deg, ${theme.gradientFrom} 0%, ${theme.gradientTo} 100%)`,
        borderColor: theme.borderColor,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16), 0 12px 40px rgba(0, 0, 0, 0.24), 0 0 1px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Title */}
      <div
        className="font-bold mb-6"
        style={{
          color: theme.textPrimary,
          fontSize: 'clamp(1.125rem, 2.5vw + 0.5rem, 1.25rem)',
        }}
      >
        Booking Details
      </div>

      {/* Details */}
      <div className="flex-1 flex flex-col gap-4">
        {serviceName && (
          <div className="flex items-start gap-3">
            <div
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: theme.borderColor,
                width: 'clamp(1.75rem, 3vw, 2rem)',
                height: 'clamp(1.75rem, 3vw, 2rem)',
              }}
            >
              <Hash
                style={{
                  color: theme.textPrimary,
                  width: 'clamp(0.875rem, 1.5vw, 1rem)',
                  height: 'clamp(0.875rem, 1.5vw, 1rem)',
                }}
              />
            </div>
            <div className="flex flex-col">
              <div
                className="uppercase tracking-wider mb-1"
                style={{
                  color: theme.textSecondary,
                  fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                }}
              >
                SERVICE
              </div>
              <div
                className="font-semibold"
                style={{
                  color: theme.textPrimary,
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                }}
              >
                {serviceName}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          <div
            className="rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: theme.borderColor,
              width: 'clamp(1.75rem, 3vw, 2rem)',
              height: 'clamp(1.75rem, 3vw, 2rem)',
            }}
          >
            <Calendar
              style={{
                color: theme.textPrimary,
                width: 'clamp(0.875rem, 1.5vw, 1rem)',
                height: 'clamp(0.875rem, 1.5vw, 1rem)',
              }}
            />
          </div>
          <div className="flex flex-col">
            <div
              className="uppercase tracking-wider mb-1"
              style={{
                color: theme.textSecondary,
                fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
              }}
            >
              DATE
            </div>
            <div
              className="font-semibold"
              style={{
                color: theme.textPrimary,
                fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
              }}
            >
              {formattedDate}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div
            className="rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: theme.borderColor,
              width: 'clamp(1.75rem, 3vw, 2rem)',
              height: 'clamp(1.75rem, 3vw, 2rem)',
            }}
          >
            <Clock
              style={{
                color: theme.textPrimary,
                width: 'clamp(0.875rem, 1.5vw, 1rem)',
                height: 'clamp(0.875rem, 1.5vw, 1rem)',
              }}
            />
          </div>
          <div className="flex flex-col">
            <div
              className="uppercase tracking-wider mb-1"
              style={{
                color: theme.textSecondary,
                fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
              }}
            >
              TIME
            </div>
            <div
              className="font-semibold"
              style={{
                color: theme.textPrimary,
                fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
              }}
            >
              {formattedTime}
            </div>
          </div>
        </div>

        {locationLabel && (
          <div className="flex items-start gap-3">
            <div
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: theme.borderColor,
                width: 'clamp(1.75rem, 3vw, 2rem)',
                height: 'clamp(1.75rem, 3vw, 2rem)',
              }}
            >
              <MapPin
                style={{
                  color: theme.textPrimary,
                  width: 'clamp(0.875rem, 1.5vw, 1rem)',
                  height: 'clamp(0.875rem, 1.5vw, 1rem)',
                }}
              />
            </div>
            <div className="flex flex-col flex-1">
              <div
                className="uppercase tracking-wider mb-1"
                style={{
                  color: theme.textSecondary,
                  fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
                }}
              >
                LOCATION
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationLabel)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-1.5 group"
                aria-label="Open location in Google Maps"
              >
                <span
                  className="font-semibold"
                  style={{
                    color: theme.textPrimary,
                    fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  }}
                >
                  {locationLabel}
                </span>
                <ArrowUpRight
                  className="transition-all group-hover:scale-110 group-hover:opacity-90 flex-shrink-0"
                  style={{
                    color: theme.accentColor,
                    opacity: 0.6,
                    width: 'clamp(1rem, 2vw, 1.25rem)',
                    height: 'clamp(1rem, 2vw, 1.25rem)',
                  }}
                />
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Code + Branding footer
           Layout: two stacked rows so confirmation code and branding
           never compete for the same horizontal space on narrow phones. */}
      <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.borderColor }}>
        <div className="flex flex-col gap-1">
          <div
            className="uppercase tracking-wider"
            style={{
              color: theme.textSecondary,
              fontSize: 'clamp(0.5625rem, 1.25vw, 0.625rem)',
            }}
          >
            CONFIRMATION CODE
          </div>
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div
              className="font-mono font-bold tracking-wider truncate flex-1 min-w-0"
              style={{
                color: theme.accentColor,
                fontSize: 'clamp(0.8125rem, 2vw, 0.9375rem)',
              }}
            >
              {publicConfirmationCode}
            </div>
            <div
              className="font-medium tracking-wide text-right flex-shrink-0 whitespace-nowrap"
              style={{
                fontSize: 'clamp(0.625rem, 1.5vw, 0.75rem)',
                color: theme.accentColor,
                opacity: 0.7,
              }}
            >
              MADE WITH FIGGY
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
